/**
 * Shared "Did you mean…?" chips for the global record-search trigram
 * suggestions returned by /api/search/global.
 *
 * Used by:
 *   - GlobalQuickFind (sidebar quick-find dropdown)
 *   - The dedicated list pages (/leads, /clients, /invoices) — they show
 *     the same chips in-page when their own search yields fewer than 3
 *     hits, so a typo earns the same correction prompt the sidebar gives.
 *
 * The /api/search/global suggestion payload is plain string[] (not the
 * KnowledgeSuggestion shape used by /knowledge), so this component is
 * deliberately separate from the knowledge SuggestionChips. Clicking a
 * chip echoes the bare term back to the parent via onSelect; the parent
 * decides whether to rewrite an input field or trigger a fresh search.
 */
export interface RecordSuggestionChipsProps {
  suggestions: string[];
  onSelect: (term: string) => void;
  containerTestId: string;
  buttonTestIdPrefix: string;
  className?: string;
}

export function RecordSuggestionChips({
  suggestions,
  onSelect,
  containerTestId,
  buttonTestIdPrefix,
  className,
}: RecordSuggestionChipsProps) {
  if (suggestions.length === 0) {
    return null;
  }
  return (
    <div className={className} data-testid={containerTestId}>
      <p className="text-xs text-muted-foreground">Did you mean…?</p>
      <div className="mt-1 flex flex-wrap gap-1">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onSelect(s)}
            className="rounded-md border bg-background px-2 py-0.5 text-xs hover-elevate active-elevate-2"
            data-testid={`${buttonTestIdPrefix}-${s}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
